import { SummarySectionTag } from "app/area/case-report/case-table-view/constants";
import { ChildCountMapping, getEvaluationGroupDetails, getEvaluations } from "app/area/case/selectors";
import { getValue } from "app/infrastructure/ui/form/selectors";
import { Entity } from "app/types-shared";
import * as caseData from 'app/area/case/case-data-model';
import { DomainAttributes } from 'app/infrastructure/ui/types-uidefinition';
import { caseTypeCategory } from "app/area/case/case-data-model";
import { getAllEntitiesAtPath } from 'app/infrastructure/store';
import { entityRowsSelector } from "app/area/case/features/file-upload/selectors";
import { endpoint } from 'app/area/case/endpoints';
import { createDownloader } from 'app/infrastructure/platform/browser';
import { fetchAttachment } from 'app/infrastructure/remote-data/http';
import { getColumnData } from "app/area/case-report/case-table-view/components/case-table-view-util";
import { FavouriteDataColumn } from "app/components/favourite-layout/favourite/common/favourite-interfaces";

const extractAttributes = ({ name, value }: DomainAttributes) => {
  const match = name.match(/\.([^.]+)$/);
  return match?.length >= 1 ? { [match[1]]: value } : { [name]: value };
};

const childEntityCount = (field: Entity[], propMappings: ChildCountMapping[], childValueItems: boolean = false) => {
  return propMappings.reduce((propObject, prop) => {
    const entityTypePath = prop.entityPath.map(d => d.entityType);
    const allRows = field.filter(item => {
      if (!item.id || !Array.isArray(item.id)) { return false }
      const itemKeys = item.id.map(obj => Object.keys(obj)[0]);
      return itemKeys.length === entityTypePath.length && entityTypePath.every(key => itemKeys.includes(key));
    });

    if (childValueItems) {
      propObject.childValueItems[prop.propName] = allRows
        .map(p => Array.isArray(p[prop.propName]) ? p[prop.propName][0] : undefined)
        .filter(x => x);
    } else {
      const filteredRows = prop.dataTag ? allRows.filter(e => !!getValue(e, prop.dataTag).value) : allRows;
      propObject[prop.propName] = {
        count: filteredRows.length,
        propName: prop.propName
      };
    }

    return propObject;
  }, childValueItems ? { ['childValueItems']: {} } : {});
};

const checklistValues = (field: Entity[]) => {
  const entityTypePath = [caseData.tableName.trans, caseData.tableName.checklistEvaluation];
  const allChecklistEvaluations = field.filter(item => {
    if (!item.id || !Array.isArray(item.id)) { return false }
    const itemKeys = item.id.map(obj => Object.keys(obj)[0]);
    return itemKeys.length === entityTypePath.length && entityTypePath.every(key => itemKeys.includes(key));
  });
  const evaluations = getEvaluations(allChecklistEvaluations);
  const result = getEvaluationGroupDetails(evaluations);
  return {
    checklistEvaluations: result,
    numberOfChecklistItems: !!result?.length ? allChecklistEvaluations?.length : 0
  };
};

const connectedCasesIdsIrrespectiveOfConnectionType = (field: Entity[]) => {
  return {
    connectedCasesIds: 
      getAllEntitiesAtPath(
        field, 
        [caseData.tableName.trans, caseData.tableName.connectedCasesSummary]
      )
      .map(e => e.primaryKey.value)
  };
};

const isBowtieSupported = (field: Entity[]) => {
  const columns = caseData.columnName[caseData.tableName.trans];
  return {
    bowtieSupported: getValue(field[0], columns.applicationCategory)?.value === caseTypeCategory.topevent
  };
};

const riskValues = (field: Entity) => {
  const columns = caseData.columnName[caseData.tableName.trans];
  return {
    riskArea: getValue(field, columns.riskArea).valueItem,
    riskWeight: getValue(field, columns.weight).value,
    riskTrend: getValue(field, columns.riskTrend).valueItems
  };
};

const residualRiskValues = (field: Entity) => {
  const columns = caseData.columnName[caseData.tableName.trans];
  return {
    riskAreaRest: getValue(field, columns.riskAreaResidual).valueItem,
    riskWeightRest: getValue(field, columns.riskWeightResidual).value,
    riskTrendRest: getValue(field, columns.riskTrendResidual).valueItems
  };
};

const integrityValues = (field: Entity) => {
  const columns = caseData.columnName[caseData.tableName.trans];
  return {
    integrityStatus: getValue(field, columns.integrityStatus).valueItem,
    integrityScore: getValue(field, columns.integrityScore).value
  };
};

const integrityStatusBarrierEvalValues = (field: Entity) => {
  const columns = caseData.columnName[caseData.tableName.trans];
  return {
    integrityStatusBarrierEval: getValue(field, columns.integrityStatusBarrierEval).valueItem
  };
};

const getThumbnails = (field: Entity[], caseId: number) => {
  const entityTypePath = [caseData.tableName.trans, caseData.tableName.object];
  const allRows = field.filter(item => {
    if (!item.id || !Array.isArray(item.id)) { return false }
    const itemKeys = item.id.map(obj => Object.keys(obj)[0]);
    return itemKeys.length === entityTypePath.length && entityTypePath.every(key => itemKeys.includes(key));
  });
  const thumbnails = entityRowsSelector(allRows, caseData.tableName.object);
  const documentUrl = endpoint.document(false, caseId);
  return {
    uploadedFiles: thumbnails.sort((a, b) => b.id - a.id),
    createDownloader: createDownloader(documentUrl),
    loadBlob: fetchAttachment(documentUrl),
    className: 'thumbnail'
  };
};

const displayCodesValues = (field: Entity[], column: Partial<FavouriteDataColumn>, value?: boolean) => {
  const columnData = getColumnData(field, column);
  if (!columnData?.length) {
    return null;
  }
  return {
    valueItems: columnData,
    style: 'secondary',
    ...(value && { value: columnData?.[0]?.value, className: 'summary-section-content' }),
    ...((column.summarySectionTag === SummarySectionTag.Status || column.summarySectionTag === SummarySectionTag.ActionStatus)
      && { displayBackgroundColor: false })
  };
};

export const getDomainAttributesForColumn = (domains: number[], domainAttributes: Record<number, DomainAttributes[]>) => {
  return domains.reduce((acc, domain) => {
    const attributes = domainAttributes[domain];
    if (attributes?.length) {
      attributes.map(extractAttributes).forEach(attr => {
        acc = { ...acc, ...attr };
      });
    }
    return acc;
  }, {});
};

export const getFactItemValues = (row: Entity[], column: Partial<FavouriteDataColumn>, caseId: number) => {
  switch (column.summarySectionTag) {
    case SummarySectionTag.Actions:
      return childEntityCount(row, [
        {
          entityPath: [{ entityType: caseData.tableName.trans }, { entityType: caseData.tableName.action }],
          propName: (caseData.columnName[caseData.tableName.action] as any).numberOfActions
        },
        {
          entityPath: [{ entityType: caseData.tableName.trans }, { entityType: caseData.tableName.action }],
          propName: (caseData.columnName[caseData.tableName.action] as any).numberOfClosedActions,
          dataTag: (caseData.columnName[caseData.tableName.action] as any).actionCompletedDate
        }]
      );
    case SummarySectionTag.Assessment:
      return childEntityCount(row, [
        {
          entityPath: [{ entityType: caseData.tableName.trans }, { entityType: caseData.tableName.assessment }],
          propName: (caseData.columnName[caseData.tableName.assessment] as any).numberOfAssessments
        }
      ]);
    case SummarySectionTag.Attachments:
      return childEntityCount(row, [
        {
          entityPath: [{ entityType: caseData.tableName.trans }, { entityType: caseData.tableName.object }],
          propName: (caseData.columnName[caseData.tableName.object] as any).numberOfFiles
        },
        {
          entityPath: [{ entityType: caseData.tableName.trans },
          { entityType: caseData.tableName.attachmentType, id: 2 },
          { entityType: caseData.tableName.link }],
          propName: (caseData.columnName[caseData.tableName.link] as any).numberOfLinks
        }
      ]);
    case SummarySectionTag.Causes:
      return childEntityCount(row, [
        {
          entityPath: [{ entityType: caseData.tableName.trans }, { entityType: caseData.tableName.causes }],
          propName: (caseData.columnName[caseData.tableName.causes] as any).numberOfCauses
        }
      ]);
    case SummarySectionTag.Comments:
      return childEntityCount(row, [
        {
          entityPath: [{ entityType: caseData.tableName.trans }, { entityType: caseData.tableName.comment }],
          propName: (caseData.columnName[caseData.tableName.comment] as any).numberOfComments
        }
      ]);
    case SummarySectionTag.InvolvedDrug:
      return childEntityCount(row, [
        {
          entityPath: [{ entityType: caseData.tableName.trans }, { entityType: caseData.tableName.involvedDrug }],
          propName: (caseData.columnName[caseData.tableName.involvedDrug] as any).numberOfInvolvedDrugs
        }
      ]);
    case SummarySectionTag.InvolvedPersons: 
      return childEntityCount(row, [
        {
          entityPath: [{ entityType: caseData.tableName.trans }, { entityType: caseData.tableName.involvedPerson }],
          propName: (caseData.columnName[caseData.tableName.involvedPerson] as any).numberOfInvolvedPersons
        }
      ]);
    case SummarySectionTag.Bowtie:
      return isBowtieSupported(row);
    case SummarySectionTag.Risk:
      return riskValues(row[0]);
    case SummarySectionTag.ResidualRisk:
      return residualRiskValues(row[0]);
    case SummarySectionTag.IntegrityStatus:
      return integrityValues(row[0]);
    case SummarySectionTag.IntegrityStatusBarrierEval:
      return integrityStatusBarrierEvalValues(row[0]);
    case SummarySectionTag.Consequences:
      return childEntityCount(row, [
        {
          entityPath: [{ entityType: caseData.tableName.trans }, { entityType: caseData.tableName.consequences }],
          propName: (caseData.columnName[caseData.tableName.consequences] as any).actConsConsequenceIcons
        }], true);
    case SummarySectionTag.CheckList:
      return checklistValues(row);
    case SummarySectionTag.ConnectedCases:
      return connectedCasesIdsIrrespectiveOfConnectionType(row);
    case SummarySectionTag.Image:
      return getThumbnails(row, caseId);
    case SummarySectionTag.Status:
    case SummarySectionTag.CaseDueStatus:
    case SummarySectionTag.ActionDueStatus:
    case SummarySectionTag.ActionStatus:
      return displayCodesValues(row, column);
    case SummarySectionTag.Confidential:
      return displayCodesValues(row, column, true);
    default:
      return {};
  }
};
